import { useState } from "react";
import { X, Building2, Loader2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

/**
 * ClaimBusinessDialog — opens from the business detail page.
 * Lets anyone (logged in or not) request ownership of a fiche.
 */
export default function ClaimBusinessDialog({ business, onClose }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/businesses/${business.id}/claim`, form);
      setSuccess(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-2 md:p-6"
      onClick={onClose}
      data-testid="claim-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl"
      >
        <header className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-lokaso-green" />
            <h3 className="font-display text-lg font-bold">Réclamer cette fiche</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100" data-testid="claim-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {success ? (
          <div className="px-6 py-10 text-center" data-testid="claim-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-lokaso-green mb-3" />
            <h4 className="font-display text-xl font-bold">Demande envoyée !</h4>
            <p className="text-stone-600 mt-2 text-sm">
              Nous avons bien reçu votre demande pour <strong>{business.name}</strong>.<br/>
              Notre équipe vous répondra par email sous 48 h.
            </p>
            <button onClick={onClose} className="btn-green mt-6">Fermer</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-5 space-y-4" data-testid="claim-form">
            <p className="text-sm text-stone-600">
              Vous êtes propriétaire ou gérant de <strong>{business.name}</strong> ? Remplissez ce formulaire pour
              prendre le contrôle de la fiche. Notre équipe vérifie sous 48 h.
            </p>

            <Field label="Nom complet *">
              <input required value={form.full_name} onChange={onChange("full_name")} className={inputCls} data-testid="claim-name-input" />
            </Field>
            <Field label="Email *">
              <input required type="email" value={form.email} onChange={onChange("email")} className={inputCls} data-testid="claim-email-input" />
            </Field>
            <Field label="Téléphone">
              <input value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+224 ..." data-testid="claim-phone-input" />
            </Field>
            <Field label="Message (preuve, lien, fonction…)">
              <textarea
                value={form.message}
                onChange={onChange("message")}
                className={`${inputCls} min-h-[90px] py-2`}
                placeholder="Indiquez votre rôle et tout élément permettant de vérifier votre identité."
                data-testid="claim-message-input"
              />
            </Field>

            {err && <p className="text-sm text-lokaso-red" data-testid="claim-error">{err}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="claim-submit"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : "Envoyer ma demande"}
            </button>
            <p className="text-[11px] text-stone-500 text-center">En soumettant, vous acceptez que Lokaso vous contacte par email.</p>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-lokaso-green";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
